<template>
    <div class="grid-container-content">
        <!-- Free Text Header -->
        <h1 v-if="header" class="col-span-full heading-brand-base text-center mb-9 p-no-margin retail:text-left retail:pl-6 logistics:md:text-left workplace:md:text-left logistics:md:col-start-2 logistics:md:col-span-7 retail:md:col-span-7 retail:md:pl-0 logistics:lg:col-start-2 logistics:lg:col-span-11 retail:lg:col-start-2 retail:lg:col-span-11 halcyon:lg:col-start-2 halcyon:lg:col-span-11" v-html="header" />
        <div :data-theme="innerTheme" class="flex flex-col md:flex-row col-span-full">
            <!-- Location Map Form -->
            <div class="location-map-controls content-gutters md:no-content-gutters flex flex-col gap-6 md:w-80 !p-6 light-theme:!text-dark-gray">
                <!-- Amenities -->
                <div class="md:grow">
                    <stockland-multi-select
                        v-if="amenities"
                        id="selectedAmenities"
                        v-model:selected-values="selectedAmenities"
                        :group="amenities"
                    />
                </div>

                <!-- Routes -->
                <div class="flex flex-col gap-3 md:gap-6 mb-4 md:mb-0">
                    <h4 class="text-brand-base sm:text-brand-lg md:text-center logistics:text-left workplace:text-left retail:text-left text-primary-text">See routes</h4>

                    <!-- Search for Route - requires map for places search -->
                    <location-map-search
                        :map="mapController ? mapController.map : null"
                        :origin="location.location"
                        @route-location="(route: any) => mapRoute = route"
                    />

                    <div v-if="locationRoutes" class="flex flex-wrap md:flex-nowrap md:flex-col gap-3 md:gap-6">
                        <button
                            v-for="route in locationRoutes"
                            :key="route.id"
                            class="location-map-route-button group"
                            @click="mapRoute = route"
                        >
                            <span class="location-map-route-button-name">{{ route.name }}</span>
                            <span class="location-map-route-button-distance">{{ `${route.distance}km` }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div class="grow">
                <gmaps-map
                    class="md:min-h-[750px] min-h-[347px] w-full aspect-square md:aspect-auto"
                    :route="decoratedRoute"
                    :origin="location.location"
                    :markers="markers"
                    :cluster="false"
                    :zoom="zoom"
                    @on-map-mount="(onMapMount)"
                    @on-map-error="(onMapError)"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, toRefs, watch } from 'vue';

import { GmapsMapController } from '../gmaps-map/gmaps-map-controller';

import { Amenity, LocationMapProperties, LocationMapState } from './interfaces';
import { MarkerData } from '../gmaps-map/interfaces';

function onMapError(error: string) {
    console.error('Map error raised:', error);
}

const useLocationMap = (props: LocationMapProperties) => {
    const intialRoute = props.locationRoutes && props.mapType === 'route' ? props.locationRoutes[0] : undefined;
    const state: LocationMapState = reactive({
        amenities: props.amenities,
        location: props.location,
        locationRoutes: props.locationRoutes,
        mapController: undefined, // Map ref
        mapRoute: intialRoute,
        selectedAmenities: props.amenities && props.mapType === 'amenity' ? props.amenities?.options.map((sA): string => sA.value as string) : [],
        zoom: props.zoom ?? undefined,
        subTheme: props.subTheme == 'dark' ? 'light' : 'dark',
    });
    const decoratedRoute = computed(() => {
        return state.mapRoute ? {
            origin: state.location.location,
            destination: state.mapRoute.location,
            markerImage: state.mapRoute.markerImage,
        } : undefined;
    })
    const markers = computed(() => {
        const originMarker: MarkerData = {
            label: props.location.label ?? null,
            markerImage: props.location.markerImage,
            location: props.location.location,
            zIndex: 100
        };
        // concat all the selected amenities markers
        let markers = [originMarker];
        for (const selectedAmenity of state.selectedAmenities) {
            const amenity = (state.amenities ? state.amenities.options : []).find((a: Amenity) => a.value === selectedAmenity );
            markers = amenity ? [...markers, ...amenity.locations] : markers;
        }
        return markers;
    })

    const onMapMount = (mC: GmapsMapController) => {
        state.mapController = mC;
    }

    // Watch for change in Selected Amenities - update map
    watch(
        () => state.selectedAmenities,
        (selectedAmenities) => {
            console.log('Selected amenities have been changed', selectedAmenities);
        }
    )

    // Watch for change in Map Route - update map
    watch(
        () => state.mapRoute,
        (mapRoute) => {
            console.log('Map route has been changed', mapRoute);
        }
    )

    return {
        state,
        decoratedRoute,
        markers,
        onMapMount
    };
}

export default defineComponent({
    name: 'location-map',
    props: {
        amenities: {
            type: Object,
            default: undefined,
        },
        header: {
            type: String,
            default: ''
        },
        locationRoutes: {
            type: Array,
            default: () => [],
        },
        location: {
            type: Object,
            required: true,
        },
        mapType: {
            type: String,
            required: true,
        },
        zoom: {
            type: [String, Number] as PropType<string | number>,
            default: undefined,
        },
        subTheme: {
            type: String,
            default: ''
        },
    },
    setup(props: LocationMapProperties) {
        const { state, decoratedRoute, markers, ...methods } = useLocationMap(props);
        return {
            ...toRefs(state),
            ...methods,
            decoratedRoute,
            markers,
            onMapError,
        };
    },
});
</script>
<style lang="postcss">

</style>
